import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useAuth } from "../../context/AuthContext";

const HERO_IMG = "https://images.unsplash.com/photo-1709626011485-6fe000ea2dbc?crop=entropy&cs=srgb&fm=jpg&q=85";
const STATS = ["240% AVG. CONVERSION LIFT", "1.2M MONTHLY IMPRESSIONS", "48 BRANDS SCALED", "AWWWARDS SOTM"];

const lines = [
  { text: "WE ENGINEER", accent: false },
  { text: "DIGITAL", accent: false },
  { text: "DOMINANCE", accent: true },
];

export default function Hero() {
  const ref = useRef(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0.15]);

  return (
    <section ref={ref} className="relative min-h-screen flex flex-col justify-end overflow-hidden">
      <motion.div style={{ y, opacity }} className="absolute inset-0">
        <img src={HERO_IMG} alt="Abstract geometric structure" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-obsidian/60 via-obsidian/40 to-obsidian" />
        <div className="absolute inset-0 bg-gradient-to-r from-obsidian/80 via-transparent to-transparent" />
      </motion.div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 w-full pt-40 pb-10">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1, duration: 0.8 }}
          className="font-mono text-[11px] uppercase tracking-[0.35em] text-volt mb-8"
        >
          Zork Media — Architects of Digital Dominance
        </motion.p>

        <h1 className="font-display font-extrabold tracking-tighter leading-[0.92] text-[8vw] sm:text-7xl lg:text-[7.5rem]">
          {lines.map((line, i) => (
            <span key={line.text} className="block overflow-hidden pb-1">
              <motion.span
                className={`block ${line.accent ? "text-volt" : "text-slate-50"}`}
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 1, delay: 0.35 + i * 0.14, ease: [0.16, 1, 0.3, 1] }}
              >
                {line.text}
              </motion.span>
            </span>
          ))}
        </h1>

        <div className="mt-10 flex flex-col sm:flex-row sm:items-end justify-between gap-8">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.15, duration: 0.8 }}
            className="max-w-md text-base md:text-lg text-slate-400 leading-relaxed"
          >
            An elite marketing agency fusing brand architecture, performance engineering and kinetic design into
            uncompromising growth machines.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.3, duration: 0.8 }}
            className="flex items-center gap-4"
          >
            <button
              data-testid="hero-cta-brief"
              onClick={() => navigate(user ? "/dashboard" : "/login")}
              className="group bg-volt text-obsidian font-mono text-xs uppercase tracking-[0.2em] font-bold px-8 py-4 flex items-center gap-2 hover:bg-[#CCEE00] transition-colors duration-300 glow-volt"
            >
              Start Project Brief
              <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
            <a
              data-testid="hero-cta-work"
              href="#work"
              className="border border-white/15 text-slate-200 font-mono text-xs uppercase tracking-[0.2em] px-8 py-4 hover:border-volt/50 hover:text-volt transition-colors duration-300"
            >
              See The Work
            </a>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5, duration: 1 }}
          className="mt-14 border-t border-white/10 pt-6 flex flex-wrap gap-x-10 gap-y-3"
          data-testid="hero-stats-ticker"
        >
          {STATS.map((s, i) => (
            <span key={s} className="font-mono text-[11px] uppercase tracking-[0.25em] text-slate-500 flex items-center gap-3">
              <span className={`w-1.5 h-1.5 ${i === 0 ? "bg-volt animate-ticker" : "bg-slate-600"}`} />
              {s}
            </span>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
